import { Dispatch, SetStateAction, useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { sheetAtom } from 'atoms/atoms'
import { useAtom, useSetAtom } from 'jotai'
import isEmpty from 'lodash-es/isEmpty'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { OptionType } from '@node-space/storybook-components/dist/components/Select/utils/selectTypes'
import { DateErrorType } from '@node-space/storybook-components/dist/DateOfBirth'
import { logSentryErrorAndGetTraceId } from '@node-space/utils'
import { dateInitialErrors, useDateInput } from 'hooks/forms/useDateInput'
import { useFormFields } from 'hooks/forms/useFormFields'
import { usePostContactMutation } from 'hooks/mutations/usePostContact'
import { usePutContactMutation } from 'hooks/mutations/usePutContactMutation'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { useToastContext } from 'hooks/useToastContext'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { BaseErrorResponse } from 'types/beneficiaries'
import { ContactEntity, ContactPayload, PutContactProps, RelationshipType } from 'types/contacts'
import { SheetName, SheetState } from 'types/types'
import { contactAtom, editingContactAtom } from '../atoms/contactAtoms'
import { ContactFormData, ContactFormKeys, contactSchema } from '../schemas/contactSchema'
import { getContactDefaultValues } from '../utils/utils'

interface UseContactFormProps {
  setDateErrors: Dispatch<SetStateAction<DateErrorType>>
}

export const useContactForm = ({ setDateErrors }: UseContactFormProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const addToast = useToastContext()
  const { formatDateInput } = useDateInput()
  const [contact, setContact] = useAtom(contactAtom)
  const setEditingContact = useSetAtom(editingContactAtom)
  const setSheet = useSetAtom(sheetAtom)
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const { mutate: postContact, isPending: isPostingContact } = usePostContactMutation()
  const { mutate: updateContact, isPending: isUpdatingContact } = usePutContactMutation()
  const { data: countries, isPending: isCountriesLoading } = useCountriesQuery(true)

  const countriesOptions: OptionType[] = countries?.map(country => ({
    label: country?.name,
    value: country?.code,
  }))

  const formSchema = contactSchema(t)

  const isEditingContact = !isEmpty(contact)

  useEffect(() => {
    setEditingContact(isEditingContact)
  }, [isEditingContact])

  const form = useForm<ContactFormData>({
    defaultValues: getContactDefaultValues(isEditingContact, contact),
    resolver: yupResolver(formSchema),
  })

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors, isSubmitting },
  } = form

  const formValues = useWatch({ control })

  const isCompany = formValues?.entityType === ContactEntity.COMPANY
  const isIndividual = formValues?.entityType === ContactEntity.INDIVIDUAL

  const { setRadioField, setInputField, setSelectField, setCheckboxField, setDatePickerField } =
    useFormFields<ContactFormKeys>(formSchema, form, formValues)

  const formFields: { [key in ContactFormKeys] } = {
    entityType: setRadioField({
      key: 'entityType',
      options: [
        { value: ContactEntity.COMPANY, label: t('contacts.company') },
        { value: ContactEntity.INDIVIDUAL, label: t('contacts.individual') },
      ],
      onChangeAdditional: () => {
        setDateErrors(dateInitialErrors)
        setValue('dateOfBirth', '')
        setValue('dateOfIncorporation', '')
        clearErrors()
        setRequestError({
          show: false,
        })
      },
    }),
    companyName: setInputField({
      key: 'companyName',
      placeholder: t('formFields.placeholder.companyName'),
    }),
    contactDescription: setInputField({
      key: 'contactDescription',
      placeholder: t('formFields.placeholder.contactDescription'),
    }),
    companyRegistrationNumber: setInputField({
      key: 'companyRegistrationNumber',
    }),
    companyRegistrationCountry: setSelectField({
      key: 'companyRegistrationCountry',
      options: countriesOptions,
      placeholder: t('select'),
    }),
    addressLine1: setInputField({
      key: 'addressLine1',
    }),
    addressLine2: setInputField({
      key: 'addressLine2',
    }),
    city: setInputField({
      key: 'city',
    }),
    postCode: setInputField({
      key: 'postCode',
    }),
    region: setInputField({
      key: 'region',
    }),
    firstName: setInputField({
      key: 'firstName',
    }),
    lastName: setInputField({
      key: 'lastName',
    }),
    countryOfResidence: setSelectField({
      key: 'countryOfResidence',
      options: countriesOptions,
      placeholder: t('select'),
    }),
    isSelfOwned: setCheckboxField({ key: 'isSelfOwned' }),
    dateOfBirth: setDatePickerField({ key: 'dateOfBirth' }),
    dateOfIncorporation: setDatePickerField({ key: 'dateOfIncorporation' }),
  }

  const onSubmit: SubmitHandler<typeof formValues> = () => {
    resetRequestError()
    const data: ContactPayload = {
      description: formValues?.contactDescription,
      relationshipType: isIndividual
        ? RelationshipType.THIRD_PARTY
        : formValues?.isSelfOwned
          ? RelationshipType.SELF_OWNED
          : RelationshipType.THIRD_PARTY,
      entity: {
        ...(isCompany && {
          legalName: formValues?.companyName,
          registrationNumber: formValues?.companyRegistrationNumber,
          dateOfIncorporation: formatDateInput(formValues?.dateOfIncorporation),
        }),
        ...(isIndividual && {
          firstName: formValues?.firstName,
          lastName: formValues?.lastName,
          dateOfBirth: formatDateInput(formValues?.dateOfBirth),
        }),
        address: {
          addressLine1: formValues?.addressLine1,
          addressLine2: formValues?.addressLine2,
          city: formValues?.city,
          postCode: formValues?.postCode,
          region: formValues?.region,
          country: isCompany
            ? formValues?.companyRegistrationCountry
            : formValues?.countryOfResidence,
        },
        type: formValues?.entityType,
      },
    }
    if (!isEditingContact) {
      postContact(data, {
        onSuccess: response => {
          addToast({
            title: t('contacts.addContactSuccess', {
              contactName: response?.name,
            }),
            state: 'success',
          })
          setContact(response)
          setSheet({
            [SheetName.INSTRUMENT]: SheetState.OPEN,
          })
          queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACTS] })
        },
        onError: (error: BaseErrorResponse) => {
          const sentryTraceId = logSentryErrorAndGetTraceId('postContact', error)
          setRequestError({
            handledMessage: error?.message,
            errorCode: error?.status,
            sentryTraceId,
            show: true,
          })
        },
      })
    } else {
      const payload: PutContactProps = {
        contact: data,
        contactId: contact?.id,
      }
      updateContact(payload, {
        onSuccess: response => {
          addToast({
            title: t('contacts.updateContactSuccess', {
              contactName: response?.name,
            }),
            state: 'success',
          })
          setContact(null)
          setSheet({
            [SheetName.CONTACT]: SheetState.CLOSED,
          })
          queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACTS] })
        },
        onError: (error: BaseErrorResponse) => {
          const sentryTraceId = logSentryErrorAndGetTraceId('updateContact', error)
          setRequestError({
            handledMessage: error?.message,
            errorCode: error?.status,
            sentryTraceId,
            show: true,
          })
        },
      })
    }
  }

  return {
    formFields,
    isCountriesLoading,
    errors,
    control,
    isSubmitting,
    isPostingContact,
    isCompany,
    isIndividual,
    requestError,
    isEditingContact,
    isUpdatingContact,
    handleSubmit,
    onSubmit,
  }
}
